import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import api from '../../../utils/api'
import RecordSelector from './RecordSelector'
import ConfirmationDialog from './ConfirmationDialog'

const ManualReconciliation = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const initialSourceId = queryParams.get('sourceId')

  // State for selected records
  const [sourceRecord, setSourceRecord] = useState(null)
  const [targetRecord, setTargetRecord] = useState(null)

  // State for reconciliation options
  const [notes, setNotes] = useState('')
  const [reconcileChildren, setReconcileChildren] = useState(false)

  // UI states
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Validation states
  const [validationErrors, setValidationErrors] = useState({
    source: '',
    target: '',
    general: '',
  })

  // Load source record if provided in URL
  useEffect(() => {
    if (initialSourceId) {
      fetchInitialRecord(initialSourceId)
    }
  }, [initialSourceId])

  const fetchInitialRecord = async (id) => {
    try {
      setLoading(true)
      const response = await api.get(`admin/reconciliation/record/${id}/`)
      setSourceRecord(response.data.record)
    } catch (err) {
      console.error('Error fetching initial record:', err)
      setValidationErrors((prev) => ({
        ...prev,
        source: 'Record not found',
      }))
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    let isValid = true
    const errors = { source: '', target: '', general: '' }

    if (!sourceRecord) {
      errors.source = 'Source record is required'
      isValid = false
    }

    if (!targetRecord) {
      errors.target = 'Target record is required'
      isValid = false
    }

    if (sourceRecord && targetRecord && sourceRecord.id === targetRecord.id) {
      errors.general = 'Source and target records cannot be the same'
      isValid = false
    }

    if (targetRecord && !targetRecord.cloudId) {
      errors.target = 'Target record must already have a cloud assignment'
      isValid = false
    }

    if (
      sourceRecord &&
      targetRecord &&
      sourceRecord.cloudId &&
      sourceRecord.cloudId === targetRecord.cloudId
    ) {
      errors.general = 'Records are already reconciled to the same cloud'
      isValid = false
    }

    setValidationErrors(errors)
    return isValid
  }

  const handleReconcile = async (e) => {
    if (e) e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)

      await api.post('admin/reconciliation/manual/', {
        sourceId: sourceRecord.id,
        targetId: targetRecord.id,
        notes,
        reconcileChildren,
      })

      setSuccess(true)
      setShowConfirmation(false)
      setError(null)

      setTimeout(() => {
        navigate(`/admin/reconciliation/record/${targetRecord.id}`)
      }, 2000)
    } catch (err) {
      console.error('Error performing manual reconciliation:', err)
      setError(`Failed to reconcile records: ${err.response?.data?.error || 'Unknown error'}`)
      setShowConfirmation(false)
    } finally {
      setLoading(false)
    }
  }

  const handleSourceSelected = (record) => {
    setSourceRecord(record)
    setValidationErrors((prev) => ({ ...prev, source: '' }))
  }

  const handleTargetSelected = (record) => {
    setTargetRecord(record)
    setValidationErrors((prev) => ({ ...prev, target: '' }))
  }

  return (
    <div className='p-4'>
      <h1 className='mb-6 text-2xl font-bold'>Manual Reconciliation</h1>

      {/* Navigation */}
      <div className='mb-6 flex border-b pb-2'>
        <Link to='/admin/reconciliation' className='px-4 py-2'>
          Dashboard
        </Link>
        <Link to='/admin/reconciliation/recent' className='px-4 py-2'>
          Recent
        </Link>
        <Link to='/admin/reconciliation/search' className='px-4 py-2'>
          Search
        </Link>
        <Link to='/admin/reconciliation/manual' className='px-4 py-2 font-bold'>
          Manual
        </Link>
      </div>

      {/* Success message */}
      {success && (
        <div className='bg-green-100 text-green-700 mb-6 rounded p-4'>
          Records successfully reconciled! Redirecting...
        </div>
      )}

      {/* Error message */}
      {error && <div className='bg-red-100 text-red-700 mb-6 rounded p-4'>{error}</div>}

      {/* General validation error */}
      {validationErrors.general && (
        <div className='bg-red-100 text-red-700 mb-6 rounded p-4'>{validationErrors.general}</div>
      )}

      {/* Main content - two column selector layout */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        <div>
          <h2 className='mb-3 text-lg font-semibold'>SOURCE RECORD</h2>
          <RecordSelector
            label='Source Record'
            record={sourceRecord}
            onRecordSelected={handleSourceSelected}
            onClear={() => setSourceRecord(null)}
            error={validationErrors.source}
            setError={setError}
          />
        </div>

        <div>
          <h2 className='mb-3 text-lg font-semibold'>TARGET RECORD</h2>
          <RecordSelector
            label='Target Record'
            record={targetRecord}
            onRecordSelected={handleTargetSelected}
            onClear={() => setTargetRecord(null)}
            error={validationErrors.target}
            setError={setError}
          />
        </div>
      </div>

      {/* Reconciliation Notes */}
      {sourceRecord && targetRecord && (
        <div className='mt-6'>
          <h2 className='mb-3 text-lg font-semibold'>RECONCILIATION NOTES</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='Enter notes about why these records should be reconciled (optional)'
            className='h-32 w-full border p-2'
          />
        </div>
      )}

      {/* Preview of Changes */}
      {sourceRecord && targetRecord && (
        <div className='mt-6'>
          <h2 className='mb-3 text-lg font-semibold'>PREVIEW OF CHANGES</h2>
          <div className='bg-gray-50 rounded border p-4'>
            <ul className='list-inside list-disc'>
              <li>
                Source record ({sourceRecord.service}) will be assigned to cloud ID{' '}
                {targetRecord.cloudId}
              </li>
              <li>All user associations will be transferred to the target cloud</li>
              <li>
                Source record's pending state will be updated to match the target cloud's status
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Reconcile Children Option */}
      {sourceRecord &&
        targetRecord &&
        (sourceRecord.mediaType === 'album' || sourceRecord.mediaType === 'playlist') && (
          <div className='mt-6'>
            <div className='flex items-center'>
              <input
                type='checkbox'
                id='reconcileChildren'
                checked={reconcileChildren}
                onChange={(e) => setReconcileChildren(e.target.checked)}
                className='mr-2 h-5 w-5'
              />
              <label htmlFor='reconcileChildren' className='font-semibold'>
                Reconcile child items (songs) too
              </label>
            </div>
            <p className='text-gray-600 ml-7 mt-1 text-sm'>
              When checked, all songs in this {sourceRecord.mediaType} will also be reconciled with
              corresponding songs in the target {sourceRecord.mediaType} based on track position.
            </p>
          </div>
        )}

      {/* Action Buttons */}
      <div className='mt-6 flex gap-4'>
        <button
          onClick={() => navigate(-1)}
          className='bg-gray-200 hover:bg-gray-300 rounded px-4 py-2'
        >
          Cancel
        </button>

        <button
          onClick={() => setShowConfirmation(true)}
          className='bg-blue-500 text-white hover:bg-blue-600 rounded px-4 py-2'
          disabled={!sourceRecord || !targetRecord || loading}
        >
          Reconcile Records
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <ConfirmationDialog
          sourceRecord={sourceRecord}
          targetRecord={targetRecord}
          reconcileChildren={reconcileChildren}
          loading={loading}
          onConfirm={handleReconcile}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </div>
  )
}

export default ManualReconciliation
